import ConfigService from "./ConfigService";

// Deeplink support was a request from bet25.
//
// A deeplink action rides on `promoId` as `dp:<action>` (e.g. `dp:shop`). The
// operator only swaps their fixed `promoId=<realId>` for `promoId=dp:shop` —
// every other param (apiKey/ts/session/hash) stays put, and since the v0 hash
// doesn't cover promoId it remains valid. We map the action to a widget surface
// and navigate this frame there, so the operator can deep-link without changing
// their embedded iframe path.
const DEEPLINK_PREFIX = "dp:";

// Maps a deeplink action (the part after `dp:`) to the widget surface it opens.
// Add new entries here as more surfaces become deep-linkable.
const ACTION_ROUTES: Record<string, string> = {
  shop: "/casino/shop",
  missions: "/casino/missions",
  badges: "/lobby/badges",
  loyalty: "/lobby/loyalty",
  casino: "/lobby/casino",
  f2p: "/f2p/lobby",
};

const isDeeplink = (promoId: string): boolean => promoId.startsWith(DEEPLINK_PREFIX);

const getAction = (promoId: string): string => promoId.slice(DEEPLINK_PREFIX.length);

/**
 * If the promoId is a deeplink (`dp:<action>`), redirects this frame to the
 * mapped widget surface. Returns true when a redirect has been initiated (the
 * caller should halt normal boot), false otherwise.
 *
 * The shop and other deep-linked surfaces resolve the operator from the auth
 * headers, not from promoId, so dropping the (now action-only) promoId is safe;
 * dropping it also prevents the target load from re-triggering this redirect.
 */
const resolveAndRedirect = (): boolean => {
  const promoId = ConfigService.getPromoId();
  if (!isDeeplink(promoId)) {
    return false;
  }

  const targetPath = ACTION_ROUTES[getAction(promoId)];
  if (!targetPath) {
    console.warn(`Unknown deeplink action in "${promoId}", continuing normal boot.`);
    return false;
  }

  // Rebuild on the current origin, keep the auth/query params, drop promoId.
  const target = new URL(window.location.href);
  target.pathname = targetPath;
  target.searchParams.delete("promoId");

  // replace() so the dp:* URL doesn't linger in history and re-trigger on Back.
  window.location.replace(target.toString());
  return true;
};

export default { isDeeplink, resolveAndRedirect };
